/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / SETTINGS-TEMPLATES — E-Mail-Vorlagen: Übersicht + Toggles
   =============================================================================
   S225 CUT A/B (Mail-Rework). Ablösung der 6 Sub-Sub-Tabs mit offenen Inline-
   Karten durch EINE chip-gefilterte Übersichtsliste:

     - Filter-Chips (Alle · Reservierungen · Bestellungen · Lieferungen · Belege
       · Exporte) statt Sub-Sub-Tabs (Jonas-Entscheid 2026-07-07).
     - Eine kompakte Zeile pro Mail-Kind: Name · Säulen-Badge · Trigger-Klartext
       · „angepasst"-Badge · Toggle (CUT B). Klick auf die Zeile öffnet das
       Preview/Edit-Modal (settings-mail-modal.js).
     - Kind-Toggle: `settings.mailKindEnabled[<kind>]` (fehlender Key = an). AUS
       = kein Outbox-INSERT (DB-Gate phase_165) bzw. kein Direktversand
       (JS-Spiegel in email-templates.js / orders-receipt-actions.js).

   Single-Source des Katalogs: src/core/mail-kinds.js (Kinds, Säulen, Chips,
   Toggle-/Override-Resolver). Non-destruktiv: Toggle/Modal-Save re-rendern nur
   die betroffene Zeile (refreshMailKindRow), Chip-Wechsel ist ein Nav-Rerender.

   ABGESCHAFFT ggü. vor-S225 (verifiziert 2026-07-07, Jonas bestätigt):
     - `useOutboxForStatusMails`-Schalter — die Live-Enqueue-Funktionen lesen das
       Flag seit phase_73c nicht mehr (toter Schalter) → ersatzlos entfernt.
     - `reservationReminderEnabled`-Schalter — vom Kind-Toggle
       `reservation_reminder_customer` übernommen (phase_165 tauscht den Check).
   Erhalten (echte Config, kein Toggle): `reservationReminderLeadHours` — als
   Inline-Dropdown in der Erinnerungsmail-Zeile.

   Anti-Circular: Inline-Handler resolven zur Laufzeit über window
   (openMailKindModal/toggleMailKind/setTemplateFilter/updateSetting).
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import {
  MAIL_KIND_CHIPS,
  getMailKind, mailKindsForChip, isMailKindEnabled,
} from '../../core/mail-kinds.js';
import { isMailKindOverridden } from '../../core/mail-kind-preview.js';
import { getTrigger } from '../../core/mail-triggers.js';
import {
  loadCustomMailRules, getCustomMailRulesCache, isCustomRulesLoaded,
  setCustomMailRuleEnabled, onCustomRulesChanged,
} from '../../core/mail-rules-data.js';
import { loadCampaigns, getCampaignsCache, isCampaignsLoaded, onCampaignsChanged } from '../../core/campaigns-data.js';
// Import zieht das Modal-Modul mit → dessen window-Bindings (openMailKindModal …)
// werden beim Laden registriert. settings-templates.js selbst wird über
// settings.js in den Bundle gezogen.
import './settings-mail-modal.js';
// S226 — Builder-Modul (window-Bindings openMailBuilderModal … registrieren).
import './settings-mail-builder.js';
// S227 — Kampagnen-Modul (window-Bindings openCampaignEditor … + renderCampaignsRows).
import { renderCampaignsRows } from './settings-campaigns.js';

// S226/S227 — Custom-Rules + Kampagnen asynchron laden; bei Änderung nur die Liste
// neu zeichnen (non-destruktiv, kein Full-Settings-Rerender).
onCustomRulesChanged(() => refreshMailKindList());
onCampaignsChanged(() => refreshMailKindList());
let _customRulesRequested = false;

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

// Chip-Filter-State (Modul-Scope; überlebt Re-Render, nicht Reload — Einstieg
// immer bei „Alle", analog zur alten Sub-Sub-Tab-Wahl).
let _templateFilter = 'all';
export function getTemplateFilter() { return _templateFilter; }
export function _setTemplateFilterForTests(v) { _templateFilter = v; }

// S225-Politur — Akkordeon-Zustand pro Kategorie in der „Alle"-Ansicht (Default:
// alles eingeklappt). Modul-State wie settings-team `team-collapse` /
// stats-collapse — überlebt den non-destruktiven Row-Re-Render. Natives
// <details> hält die Sichtbarkeit selbst; das Set reproduziert nur den Zustand
// bei einem vollen Re-Render (renderSettings).
const _openMailCategories = new Set();
export function _resetMailCategoriesForTests() { _openMailCategories.clear(); }

/** Warntext für kritische Kinds im deaktivierten Zustand (Belege/Storno). */
function _criticalWarnText(meta) {
  if (meta.pillar === 'bons' || meta.pillar === 'exporte' || meta.kind === 'delivery_receipt') {
    return 'Beleg-Zustellung deaktiviert. Bon-/Z-Bon-PDF und Archiv bleiben erhalten (GoBD) — nur die Mail geht nicht mehr raus.';
  }
  return 'Storno-Benachrichtigung deaktiviert — der Gast wird bei einem Storno nicht mehr per Mail informiert.';
}

/** Eine Übersichtszeile für ein Mail-Kind (System-Look: .label/.desc + .toggle-
 *  Switch). KEINE Säulen-Badge mehr — die steht schon im Gruppen-/Chip-Kopf
 *  (redundanzfrei, Jonas-UI-Feedback 2026-07-07). */
export function renderMailKindRow(kind) {
  const meta = getMailKind(kind);
  if (!meta) return '';
  const settings = getState().settings || {};
  const enabled = isMailKindEnabled(settings, kind);
  const overridden = isMailKindOverridden(settings, kind);
  const leadHours = meta.hasLeadHours ? Number(settings.reservationReminderLeadHours ?? 24) : null;

  const leadCtl = leadHours != null
    ? `<label class="mail-kind-lead">Vorlauf
         <select onchange="updateSetting('reservationReminderLeadHours', Number(this.value))">
           ${[4, 12, 24, 48].map(h => `<option value="${h}" ${h === leadHours ? 'selected' : ''}>${h} h</option>`).join('')}
         </select></label>`
    : '';

  const warn = (!enabled && meta.critical)
    ? `<div class="mail-kind-warn">⚠️ ${htmlEscape(_criticalWarnText(meta))}</div>`
    : '';

  // S226 — noToggle-Kinds (newsletter_confirm): „immer aktiv" statt Schalter.
  const ctl = meta.noToggle
    ? `<span class="badge-soft" style="font-size:.68rem;white-space:nowrap;" title="Ohne diese Bestätigungsmail ist kein Newsletter-Abo möglich.">immer aktiv</span>`
    : `<label class="toggle" title="${enabled ? 'aktiv — Klick zum Deaktivieren' : 'aus — Klick zum Aktivieren'}">
            <input type="checkbox" ${enabled ? 'checked' : ''} onchange="toggleMailKind('${htmlEscape(kind)}', this.checked)">
            <span class="toggle-slider"></span>
          </label>`;

  return `
    <div class="mail-kind-row${enabled ? '' : ' is-off'}" data-mail-kind-row="${htmlEscape(kind)}">
      <div class="mail-kind-row-main">
        <button type="button" class="mail-kind-open" onclick="openMailKindModal('${htmlEscape(kind)}')">
          <span class="label">${htmlEscape(meta.label)}${meta.critical ? ' <span class="mail-kind-crit" title="GoBD-/vertragsrelevant" aria-label="kritisch">⚠️</span>' : ''}${overridden ? ' <span class="mail-kind-badge">angepasst</span>' : ''}</span>
          <span class="desc">${htmlEscape(meta.trigger)}</span>
        </button>
        <div class="mail-kind-ctl">
          ${leadCtl}
          ${ctl}
        </div>
      </div>
      ${warn}
    </div>`;
}

/** S226 — Zeile für eine eigene Mail-Regel (Builder). Öffnen → Builder-Modal;
 *  eigener enabled-Toggle; Trigger-Klartext aus mail-triggers.js. */
export function renderCustomRuleRow(rule) {
  if (!rule) return '';
  const trig = getTrigger(rule.trigger_event);
  const trigLabel = trig ? trig.label : rule.trigger_event;
  const recip = rule.recipient_mode === 'fixed'
    ? `an ${htmlEscape(rule.fixed_email || 'feste Adresse')}`
    : 'an Abonnenten';
  const enabled = rule.enabled !== false;
  const offsetTxt = (trig && trig.timeAnchored && rule.offset_minutes)
    ? ` · ${rule.offset_minutes > 0 ? '+' : ''}${rule.offset_minutes} Min.` : '';
  return `
    <div class="mail-kind-row${enabled ? '' : ' is-off'}" data-custom-rule-row="${htmlEscape(rule.id)}">
      <div class="mail-kind-row-main">
        <button type="button" class="mail-kind-open" onclick="openMailBuilderModal('${htmlEscape(rule.id)}')">
          <span class="label">${htmlEscape(rule.name || '(ohne Namen)')}</span>
          <span class="desc">${htmlEscape(trigLabel)}${offsetTxt} · ${recip}</span>
        </button>
        <div class="mail-kind-ctl">
          <label class="toggle" title="${enabled ? 'aktiv — Klick zum Deaktivieren' : 'aus — Klick zum Aktivieren'}">
            <input type="checkbox" ${enabled ? 'checked' : ''} onchange="toggleCustomRule('${htmlEscape(rule.id)}', this.checked)">
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>`;
}

/** Eigene-Mails-Zeilen (aus dem Cache) + „+ Neue Mail"-Button + Leerzustand. */
function _renderCustomRulesRows() {
  const rules = getCustomMailRulesCache();
  const addBtn = `
    <div style="padding:12px 16px;">
      <button type="button" class="btn btn-secondary btn-sm" onclick="openMailBuilderModal()">+ Neue Mail</button>
    </div>`;
  if (!isCustomRulesLoaded()) {
    return `<p style="padding:16px;color:var(--ink-soft);">Eigene Mails werden geladen …</p>` + addBtn;
  }
  if (!rules.length) {
    return `<p style="padding:16px;color:var(--ink-soft);">Noch keine eigenen Mails angelegt.</p>` + addBtn;
  }
  return rules.map(r => renderCustomRuleRow(r)).join('') + addBtn;
}

/** Toggle für eine eigene Regel (non-destruktiv: nur die Zeile neu zeichnen). */
export async function toggleCustomRule(id, enabled) {
  try {
    await setCustomMailRuleEnabled(id, enabled);
  } catch (e) {
    console.error('[toggleCustomRule]', e);
    if (typeof window.showToast === 'function') window.showToast('Konnte den Status nicht ändern.', 'error');
  }
  // onCustomRulesChanged → refreshMailKindList zeichnet die Liste neu.
}

/** Statuszeile pro Kategorie-Kopf: „N Arten" + wie viele deaktiviert. */
function _categorySummaryMeta(kinds) {
  const settings = getState().settings || {};
  const off = kinds.filter(k => !isMailKindEnabled(settings, k.kind)).length;
  const n = kinds.length;
  const artenLabel = `${n} ${n === 1 ? 'Art' : 'Arten'}`;
  const offLabel = off > 0
    ? `<span style="color:#e7a82d;">${off} aus</span>`
    : '<span style="color:var(--ink-muted,#8b9890);">alle aktiv</span>';
  return `${artenLabel} · ${offLabel}`;
}

function _renderList(filter) {
  // S226 — Eigene-Mails-Chip: Builder-Regeln statt System-Kinds.
  if (filter === 'eigene') {
    return _renderCustomRulesRows();
  }
  // S227 — Kampagnen-Chip.
  if (filter === 'kampagnen') {
    return renderCampaignsRows();
  }
  if (filter && filter !== 'all') {
    // Gefilterte Ansicht: der Chip IST der Kategorie-Kopf → Zeilen direkt (offen).
    const rows = mailKindsForChip(filter).map(k => renderMailKindRow(k.kind)).join('');
    return rows || '<p style="padding:16px;color:var(--ink-soft);">Keine Mails in dieser Kategorie.</p>';
  }
  // „Alle" → je Säule ein eingeklapptes Akkordeon (Default zu, bei Bedarf auf).
  // Muster = settings-team `.team-collapse` (natives <details> + Modul-State).
  const kindGroups = MAIL_KIND_CHIPS.filter(c => c.id !== 'all').map(c => {
    const kinds = mailKindsForChip(c.id);
    if (!kinds.length) return '';
    const open = _openMailCategories.has(c.id);
    const rows = kinds.map(k => renderMailKindRow(k.kind)).join('');
    return `
      <details class="mail-kind-group" data-mail-group="${c.id}" ${open ? 'open' : ''}>
        <summary onclick="toggleMailCategory('${c.id}')">
          <span class="mail-kind-group-title">${c.icon} ${htmlEscape(c.label)}</span>
          <span class="mail-kind-group-meta">${_categorySummaryMeta(kinds)}</span>
        </summary>
        <div class="mail-kind-group-body">${rows}</div>
      </details>`;
  }).join('');
  // S226 — Eigene-Mails-Akkordeon ans Ende (eigenes Toggle je Regel).
  const rules = getCustomMailRulesCache();
  const openE = _openMailCategories.has('eigene');
  const eigeneMeta = isCustomRulesLoaded()
    ? `${rules.length} ${rules.length === 1 ? 'Mail' : 'Mails'}`
    : '…';
  const eigeneGroup = `
    <details class="mail-kind-group" data-mail-group="eigene" ${openE ? 'open' : ''}>
      <summary onclick="toggleMailCategory('eigene')">
        <span class="mail-kind-group-title">✍️ Eigene Mails</span>
        <span class="mail-kind-group-meta">${eigeneMeta}</span>
      </summary>
      <div class="mail-kind-group-body">${_renderCustomRulesRows()}</div>
    </details>`;
  // S227 — Kampagnen-Akkordeon.
  const camps = getCampaignsCache();
  const openC = _openMailCategories.has('kampagnen');
  const campMeta = isCampaignsLoaded()
    ? `${camps.length} ${camps.length === 1 ? 'Kampagne' : 'Kampagnen'}`
    : '…';
  const campGroup = `
    <details class="mail-kind-group" data-mail-group="kampagnen" ${openC ? 'open' : ''}>
      <summary onclick="toggleMailCategory('kampagnen')">
        <span class="mail-kind-group-title">📣 Kampagnen</span>
        <span class="mail-kind-group-meta">${campMeta}</span>
      </summary>
      <div class="mail-kind-group-body">${renderCampaignsRows()}</div>
    </details>`;
  return kindGroups + eigeneGroup + campGroup;
}

/** S226 — nur den Listen-Container neu zeichnen (non-destruktiv, kein
 *  Full-Settings-Rerender). Aufgerufen vom Custom-Rules-Change-Hook. */
export function refreshMailKindList() {
  const host = document.getElementById('mail-kind-list');
  if (host) host.innerHTML = _renderList(_templateFilter);
}

/** Akkordeon-Toggle (Kategorie in der „Alle"-Ansicht). Hält den Zustand im
 *  Modul-Set; das native <details> übernimmt die Sichtbarkeit selbst — kein
 *  Re-Render nötig (gg-non-destructive-render). */
export function toggleMailCategory(cat) {
  if (_openMailCategories.has(cat)) _openMailCategories.delete(cat);
  else _openMailCategories.add(cat);
}

// Anzahl der Kinds (aus dem Katalog abgeleitet, kein Magic-Literal).
const MAIL_KINDS_COUNT = mailKindsForChip('all').length;

// S226 — Chip-Leiste = 6 Säulen + Newsletter (aus mail-kinds) + „Eigene Mails"
// (Builder-Regeln, kein Mail-Kind → hier angehängt).
const TEMPLATE_CHIPS = MAIL_KIND_CHIPS.concat([
  { id: 'eigene', label: 'Eigene Mails', icon: '✍️' },
  { id: 'kampagnen', label: 'Kampagnen', icon: '📣' },
]);

/** Haupt-Renderer des Vorlagen-Tabs: Chip-Leiste + Übersichtsliste. */
export function renderTemplatesOverview() {
  // Custom-Rules + Kampagnen einmalig anfordern (async) → bei Ankunft refreshMailKindList.
  if (!_customRulesRequested) {
    _customRulesRequested = true;
    loadCustomMailRules().then(() => refreshMailKindList()).catch(() => {});
    loadCampaigns().then(() => refreshMailKindList()).catch(() => {});
  }
  const chips = TEMPLATE_CHIPS.map(c => `
    <button type="button" role="tab" aria-selected="${c.id === _templateFilter}"
            class="rules-subtab ${c.id === _templateFilter ? 'active' : ''}"
            onclick="setTemplateFilter('${c.id}')">
      <span class="rules-subtab-icon" aria-hidden="true">${c.icon}</span>
      <span class="rules-subtab-label">${htmlEscape(c.label)}</span>
    </button>`).join('');

  return `
    <div class="rules-subtabs" role="tablist" aria-label="Mail-Kategorien">${chips}</div>
    <div class="card">
      <h2>E-Mail-Vorlagen <span class="badge-soft" style="font-size:.7rem;">${MAIL_KINDS_COUNT} Mail-Arten</span></h2>
      <p style="color:var(--ink-soft);font-size:.9rem;margin:0 0 4px;">
        Jede Mail-Art in einer Zeile — Klick öffnet die Vorschau (Testdaten) und den Editor.
        Mit dem Schalter rechts schaltest du eine Mail-Art komplett an oder aus:
        <strong>aus</strong> heißt, es wird keine solche Mail mehr versendet.
      </p>
      <p style="color:var(--ink-muted,#8b9890);font-size:.82rem;margin:0 0 12px;">
        ⚠️ markierte Arten sind Belege oder Storno-Benachrichtigungen — deaktivieren
        stoppt nur die Zustellung, die Belege selbst (PDF/Archiv) bleiben erhalten.
      </p>
      <div id="mail-kind-list" style="margin:0 -16px -16px;">${_renderList(_templateFilter)}</div>
    </div>`;
}

/** Chip-Wechsel → volle Neuzeichnung des Settings-Tabs (Nav-Aktion). */
export function setTemplateFilter(cat) {
  if (!TEMPLATE_CHIPS.some(c => c.id === cat)) return;
  _templateFilter = cat;
  if (typeof window.renderSettings === 'function') window.renderSettings();
}

/**
 * CUT B — Kind-Toggle. Nur der deaktivierte Zustand wird als expliziter
 * `false`-Key gespeichert (aktivieren löscht den Key → Slot bleibt schlank,
 * kein Backfill nötig). Non-destruktiv: nur die betroffene Zeile re-rendern.
 */
export async function toggleMailKind(kind, enabled) {
  if (!getMailKind(kind)) return;
  const state = getState();
  const cur = { ...((state.settings && state.settings.mailKindEnabled) || {}) };
  if (enabled) delete cur[kind];
  else cur[kind] = false;
  if (typeof window.updateSetting === 'function') {
    await window.updateSetting('mailKindEnabled', cur);
  }
  refreshMailKindRow(kind);
  refreshMailCategoryHead(kind);   // S225-HF7 — Kopf-Zähler „N Arten · X aus" mitziehen
}

/** Ersetzt nur die betroffene Zeile im DOM (Badge/Toggle/Warnhinweis) —
 *  gg-non-destructive-render. Neue Inline-Handler werden vom MutationObserver
 *  (dom-bindings) automatisch verkabelt. */
export function refreshMailKindRow(kind) {
  const el = document.querySelector(`[data-mail-kind-row="${CSS && CSS.escape ? CSS.escape(kind) : kind}"]`);
  if (!el) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderMailKindRow(kind).trim();
  const next = tmp.firstElementChild;
  if (next && el.parentNode) el.parentNode.replaceChild(next, el);
}

/** S225-HF7 — aktualisiert non-destruktiv NUR die Statuszeile des Gruppen-Kopfs
 *  („N Arten · X aus") der Kategorie des Kinds. No-op in der gefilterten
 *  Chip-Ansicht (dort gibt es kein Akkordeon) — dann fehlt das data-mail-group-
 *  Element schlicht. Ergänzt refreshMailKindRow beim Toggle. */
export function refreshMailCategoryHead(kind) {
  const meta = getMailKind(kind);
  if (!meta) return;
  const cat = meta.pillar;
  const catSel = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(cat) : cat;
  const head = document.querySelector(`[data-mail-group="${catSel}"] .mail-kind-group-meta`);
  if (head) head.innerHTML = _categorySummaryMeta(mailKindsForChip(cat));
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    setTemplateFilter,
    toggleMailCategory,
    toggleMailKind,
    refreshMailKindRow,
    renderMailKindRow,
    toggleCustomRule,
    refreshMailKindList,
  });
}
